import { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  createEventCoupon,
  deleteEventCoupon,
  listEventCoupons,
  updateEventCoupon,
} from "../services/api";

/**
 * CouponsCard — gerência de cupons de desconto (% por agora) por evento.
 *
 * Apenas para eventos pagos. Cupons não acumulam com o desconto de grupo
 * (a regra fica no backend; a UI só mostra um aviso para o admin).
 *
 * Props:
 *   - eventId: string (obrigatório)
 *   - groupMinSize: number | null (apenas para exibir o aviso correto)
 *   - eventMode: "paid" | "free_with_donation"
 */
export default function CouponsCard({ eventId, groupMinSize, eventMode }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    const response = await listEventCoupons(eventId);
    setLoading(false);
    if (response instanceof Error) {
      toast.error(response.message);
      return;
    }
    setCoupons(Array.isArray(response.data) ? response.data : []);
  }, [eventId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (eventMode !== "paid") {
    return null;
  }

  const openCreate = () => {
    setEditing(null);
    setShowModal(true);
  };
  const openEdit = (coupon) => {
    setEditing(coupon);
    setShowModal(true);
  };

  const handleDelete = async (coupon) => {
    if (
      !window.confirm(
        `Excluir o cupom "${coupon.code}"? Inscrições existentes que usaram esse cupom permanecem registradas, mas o código deixa de funcionar.`
      )
    ) {
      return;
    }
    const response = await deleteEventCoupon(eventId, coupon.id);
    if (response instanceof Error) {
      toast.error(response.message);
      return;
    }
    toast.success("Cupom excluído");
    refresh();
  };

  return (
    <Card className="border-0 shadow-sm mb-6">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider m-0">
            Cupons de desconto
          </h2>
          <p className="text-xs text-slate-500 mt-1 mb-0">
            Cupons percentuais aplicados sobre o preço unitário.
            {groupMinSize ? (
              <>
                {" "}
                Não acumulam com o desconto automático de grupo (a partir de{" "}
                {groupMinSize} pessoas).
              </>
            ) : null}
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={openCreate}
          disabled={!eventId}
        >
          + Novo cupom
        </Button>
      </div>

      <div className="p-5">
        {!eventId ? (
          <Alert variant="info" className="mb-0">
            Salve o evento primeiro para começar a cadastrar cupons.
          </Alert>
        ) : loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Carregando cupons...
          </div>
        ) : coupons.length === 0 ? (
          <Alert variant="light" className="mb-0">
            Nenhum cupom cadastrado para este evento.
          </Alert>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500">
                <th>Código</th>
                <th>Desconto</th>
                <th>Usos</th>
                <th>Status</th>
                <th className="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id}>
                  <td className="font-mono">{c.code}</td>
                  <td>{c.discount_value}%</td>
                  <td>
                    {c.uses_count}
                    {c.max_uses != null ? ` / ${c.max_uses}` : " / ∞"}
                  </td>
                  <td>
                    {c.active ? (
                      <Badge bg="success">Ativo</Badge>
                    ) : (
                      <Badge bg="secondary">Inativo</Badge>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => openEdit(c)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(c)}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <CouponModal
        show={showModal}
        coupon={editing}
        eventId={eventId}
        onClose={() => setShowModal(false)}
        onSaved={() => {
          setShowModal(false);
          refresh();
        }}
      />
    </Card>
  );
}

function CouponModal({ show, coupon, eventId, onClose, onSaved }) {
  const isEdit = !!coupon;
  const [code, setCode] = useState("");
  const [discountValue, setDiscountValue] = useState("50");
  const [maxUses, setMaxUses] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setCode(coupon?.code ?? "");
      setDiscountValue(String(coupon?.discount_value ?? "50"));
      setMaxUses(coupon?.max_uses == null ? "" : String(coupon.max_uses));
      setActive(coupon?.active ?? true);
    }
  }, [show, coupon]);

  const handleSave = async () => {
    const normalizedCode = code.trim().toUpperCase();
    if (!/^[A-Z0-9_-]{3,40}$/.test(normalizedCode)) {
      toast.error(
        "Código deve ter 3-40 caracteres (A-Z, 0-9, _ ou -)."
      );
      return;
    }
    const value = parseInt(discountValue, 10);
    if (!Number.isInteger(value) || value < 1 || value > 100) {
      toast.error("Desconto deve ser um inteiro entre 1 e 100.");
      return;
    }
    let maxUsesPayload = null;
    if (maxUses !== "") {
      const m = parseInt(maxUses, 10);
      if (!Number.isInteger(m) || m < 0) {
        toast.error("Máximo de usos deve ser inteiro >= 0 (ou vazio).");
        return;
      }
      maxUsesPayload = m;
    }

    setSaving(true);
    const payload = {
      code: normalizedCode,
      discount_value: value,
      max_uses: maxUsesPayload,
      active,
    };
    const response = isEdit
      ? await updateEventCoupon(eventId, coupon.id, payload)
      : await createEventCoupon(eventId, payload);
    setSaving(false);
    if (response instanceof Error) {
      toast.error(response.message);
      return;
    }
    toast.success(isEdit ? "Cupom atualizado" : "Cupom criado");
    onSaved();
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? `Editar cupom ${coupon?.code}` : "Novo cupom"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Código</Form.Label>
          <Form.Control
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Ex.: PARCEIRO50"
            maxLength={40}
            autoFocus
          />
          <Form.Text className="text-muted">
            Apenas letras, números, _ e -. Convertido para MAIÚSCULAS.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Desconto (%)</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={100}
            step={1}
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Máximo de usos</Form.Label>
          <Form.Control
            type="number"
            min={0}
            step={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="Deixe vazio para ilimitado"
          />
          {isEdit && coupon ? (
            <Form.Text className="text-muted">
              Usos atuais: {coupon.uses_count}
            </Form.Text>
          ) : null}
        </Form.Group>

        <Form.Group className="mb-1">
          <Form.Check
            type="switch"
            id="coupon-active-switch"
            label={active ? "Ativo" : "Inativo"}
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
